/**
 * An assistant reply, rendered.
 *
 * `markdownParse.ts` turns the text into a tree; this turns the tree into
 * React nodes against the design tokens. Nothing here reads HTML — see the
 * parser's header for why that is the whole security story. Re-parsed on every
 * token while a reply streams; a reply is a few kilobytes and the parse is a
 * single pass, so memoising on the text is all it needs.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseCodeText, type List, type Span } from './codeText';
import { highlight } from './highlight';
import { parseMarkdown, type Block, type Inline } from './markdownParse';
import { Ph } from './ui';

/** Inline code, here, in a person's message and in the composer. Units are
 *  written out so the composer can put it straight on a DOM element. */
export const inlineCode = {
  fontFamily: 'var(--font-mono)',
  fontSize: '.86em',
  color: 'var(--primary-text)',
  background: 'var(--app-panel-sunken)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '5px',
  padding: '1px 5px'
} satisfies React.CSSProperties;

const link: React.CSSProperties = {
  color: 'var(--text-link)',
  textDecoration: 'underline',
  textUnderlineOffset: 2
};

const HEADING_SIZE: Record<number, number> = { 1: 20, 2: 18, 3: 16 };

function inlines(nodes: Inline[], key: string): React.ReactNode[] {
  return nodes.map((n, i) => {
    const k = `${key}.${i}`;
    switch (n.t) {
      case 'text':
        return n.v;
      case 'br':
        return <br key={k} />;
      case 'code':
        return (
          <code key={k} style={inlineCode}>
            {n.v}
          </code>
        );
      case 'strong':
        return (
          <strong key={k} style={{ fontWeight: 600, color: 'var(--text-strong)' }}>
            {inlines(n.c, k)}
          </strong>
        );
      case 'em':
        return <em key={k}>{inlines(n.c, k)}</em>;
      case 'del':
        return <del key={k}>{inlines(n.c, k)}</del>;
      case 'link':
        return (
          <a key={k} href={n.href} target="_blank" rel="noopener noreferrer" style={link}>
            {inlines(n.c, k)}
          </a>
        );
    }
  });
}

/** The code block's copy control. Its own hover, because the block is dark in
 *  both themes and the shared `.hov-*` washes are drawn for light panels. */
function CopyCode({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const [hover, setHover] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      className="bare tap"
      aria-label={copied ? 'Copied' : 'Copy code'}
      title={copied ? 'Copied' : 'Copy code'}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1500);
        });
      }}
      style={{
        width: 28,
        height: 26,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 6,
        color: hover ? 'var(--slate-100)' : 'var(--slate-400)',
        background: hover ? 'rgba(233,233,237,0.08)' : 'transparent'
      }}
    >
      <Ph name={copied ? 'check' : 'copy'} size={14} />
    </button>
  );
}

export function CodeBlock({ lang, text }: { lang: string; text: string }) {
  const runs = useMemo(() => highlight(text, lang), [text, lang]);
  return (
    <div style={{ background: 'var(--surface-code)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', height: 32, padding: '0 6px 0 14px' }}>
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: 'var(--ls-label)',
            textTransform: 'uppercase',
            color: 'var(--slate-500)'
          }}
        >
          {lang}
        </span>
        <CopyCode text={text} />
      </div>
      <pre
        style={{
          margin: 0,
          padding: '2px 14px 14px',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          lineHeight: 1.6,
          color: 'var(--slate-200)',
          tabSize: 2
        }}
      >
        <code>
          {runs.map((r, i) =>
            r.colour ? (
              <span key={i} style={{ color: r.colour }}>
                {r.text}
              </span>
            ) : (
              r.text
            )
          )}
        </code>
      </pre>
    </div>
  );
}

const stack: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 };

/** A list and its items, in a reply and in a person's message. */
const listStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 4
};
const itemStyle: React.CSSProperties = { paddingLeft: 2 };

function blocks(list: Block[], key: string): React.ReactNode[] {
  return list.map((b, i) => {
    const k = `${key}.${i}`;
    switch (b.t) {
      case 'p':
        return (
          <p key={k} style={{ margin: 0, textWrap: 'pretty' } as React.CSSProperties}>
            {inlines(b.c, k)}
          </p>
        );
      case 'h':
        return (
          <div
            key={k}
            role="heading"
            aria-level={b.level}
            style={{
              marginTop: 4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600,
              fontSize: HEADING_SIZE[b.level] ?? 15,
              lineHeight: 1.35,
              color: 'var(--text-strong)'
            }}
          >
            {inlines(b.c, k)}
          </div>
        );
      case 'code':
        return <CodeBlock key={k} lang={b.lang} text={b.text} />;
      case 'quote':
        return (
          <blockquote
            key={k}
            style={{
              ...stack,
              margin: 0,
              padding: '2px 0 2px 14px',
              borderLeft: '3px solid var(--border-strong)',
              color: 'var(--text-muted)'
            }}
          >
            {blocks(b.c, k)}
          </blockquote>
        );
      case 'list': {
        const items = b.items.map((item, j) => (
          <li key={j} style={itemStyle}>
            <div style={{ ...stack, gap: 6 }}>{blocks(item, `${k}.${j}`)}</div>
          </li>
        ));
        return b.ordered ? (
          <ol key={k} start={b.start} style={listStyle}>
            {items}
          </ol>
        ) : (
          <ul key={k} style={listStyle}>
            {items}
          </ul>
        );
      }
      case 'table':
        return (
          <div key={k} className="scroll-x">
            <table style={{ borderCollapse: 'collapse', fontSize: 14, lineHeight: 1.5 }}>
              <thead>
                <tr>
                  {b.head.map((cell, c) => (
                    <th
                      key={c}
                      style={{
                        textAlign: b.align[c] ?? 'left',
                        padding: '7px 12px',
                        fontWeight: 600,
                        color: 'var(--text-strong)',
                        borderBottom: '1px solid var(--border-strong)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {inlines(cell, `${k}.h${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td
                        key={c}
                        style={{
                          textAlign: b.align[c] ?? 'left',
                          padding: '7px 12px',
                          borderBottom: '1px solid var(--border-subtle)',
                          verticalAlign: 'top'
                        }}
                      >
                        {inlines(cell, `${k}.${r}.${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'hr':
        return (
          <hr key={k} style={{ border: 'none', borderTop: '1px solid var(--border-subtle)', margin: '4px 0' }} />
        );
    }
  });
}

export const Markdown = React.memo(function Markdown({ text }: { text: string }) {
  const tree = useMemo(() => parseMarkdown(text), [text]);
  return (
    <div
      className="selectable"
      style={{
        ...stack,
        gap: 12,
        fontSize: 15,
        lineHeight: 1.65,
        color: 'var(--text-body)',
        overflowWrap: 'break-word'
      }}
    >
      {blocks(tree, 'm')}
    </div>
  );
});

function spans(list: Span[], key: string): React.ReactNode[] {
  return list.map((s, i) =>
    s.code ? (
      <code key={`${key}.${i}`} style={inlineCode}>
        {s.text}
      </code>
    ) : (
      s.text
    )
  );
}

/** A list in a person's message: each item's line as typed, and the lists
 *  nested under it. */
function codeList(list: List, key: string): React.ReactNode {
  const items = list.items.map((item, j) => (
    <li key={j} style={itemStyle}>
      <div style={{ whiteSpace: 'pre-wrap' }}>{spans(item.spans, `${key}.${j}`)}</div>
      {item.lists.length ? (
        <div style={{ ...stack, gap: 4, marginTop: 4 }}>{item.lists.map((l, n) => codeList(l, `${key}.${j}.${n}`))}</div>
      ) : null}
    </li>
  ));
  return list.ordered ? (
    <ol key={key} start={list.start} style={listStyle}>
      {items}
    </ol>
  ) : (
    <ul key={key} style={listStyle}>
      {items}
    </ul>
  );
}

/**
 * A person's message: their lines as typed, with inline code, fenced blocks
 * and lists drawn as a reply's are (`codeText.ts`). Blank lines around a
 * block or a list are its gap. `lead` goes in front of the first line.
 */
export const CodeText = React.memo(function CodeText({ text, lead }: { text: string; lead?: React.ReactNode }) {
  const parts = useMemo(() => {
    const out: Array<{ t: 'lines'; lines: Span[][] } | { t: 'fence'; lang: string; text: string } | { t: 'list'; list: List }> = [];
    for (const p of parseCodeText(text)) {
      if (p.t !== 'line') out.push(p);
      else {
        const last = out[out.length - 1];
        if (last?.t === 'lines') last.lines.push(p.spans);
        else out.push({ t: 'lines', lines: [p.spans] });
      }
    }
    for (const part of out) {
      if (part.t !== 'lines') continue;
      while (part.lines.length && !part.lines[0]!.length) part.lines.shift();
      while (part.lines.length && !part.lines[part.lines.length - 1]!.length) part.lines.pop();
    }
    return out.filter((part) => part.t !== 'lines' || part.lines.length);
  }, [text]);

  const first = parts[0];
  return (
    <div className="selectable" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {lead && first?.t !== 'lines' ? <div>{lead}</div> : null}
      {parts.map((part, i) =>
        part.t === 'fence' ? (
          <CodeBlock key={i} lang={part.lang} text={part.text} />
        ) : part.t === 'list' ? (
          codeList(part.list, String(i))
        ) : (
          <div key={i} style={{ whiteSpace: 'pre-wrap' }}>
            {i === 0 ? lead : null}
            {part.lines.map((line, j) => (
              <React.Fragment key={j}>
                {j ? '\n' : null}
                {spans(line, `${i}.${j}`)}
              </React.Fragment>
            ))}
          </div>
        )
      )}
    </div>
  );
});
